/**
 * 内存监控工具
 * 用于检测和预防内存泄漏
 */

interface MemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
  timestamp: number;
  route?: string;
}

class MemoryMonitor {
  private isMonitoring = false;
  private monitorTimer: number | null = null;
  private memoryHistory: MemoryInfo[] = [];
  private readonly MAX_HISTORY_LENGTH = 100;
  private readonly MEMORY_THRESHOLD = 50 * 1024 * 1024; // 50MB
  private readonly CHECK_INTERVAL = 5000; // 5秒

  /**
   * 开始内存监控
   */
  startMonitoring() {
    if (this.isMonitoring || !this.isMemoryAPIAvailable()) {
      return;
    }

    this.isMonitoring = true;
    this.monitorTimer = window.setInterval(() => {
      this.checkMemoryUsage();
    }, this.CHECK_INTERVAL);

  }

  /**
   * 停止内存监控
   */
  stopMonitoring() {
    if (this.monitorTimer) {
      clearInterval(this.monitorTimer);
      this.monitorTimer = null;
    }
    this.isMonitoring = false;
  }

  /**
   * 检查浏览器是否支持内存API
   */
  private isMemoryAPIAvailable(): boolean {
    return !!(performance as any).memory;
  }

  /**
   * 检查内存使用情况
   */
  private checkMemoryUsage() {
    if (!this.isMemoryAPIAvailable()) return;

    const memory = (performance as any).memory;
    const memoryInfo: MemoryInfo = {
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit,
      timestamp: Date.now(),
      route: window.location.hash
    };

    this.memoryHistory.push(memoryInfo);

    // 保持历史记录长度
    if (this.memoryHistory.length > this.MAX_HISTORY_LENGTH) {
      this.memoryHistory.shift();
    }

    // 检查内存泄漏
    this.detectMemoryLeak(memoryInfo);
  }

  /**
   * 检测内存泄漏
   */
  private detectMemoryLeak(currentMemory: MemoryInfo) {
    // 如果内存使用超过阈值，发出警告
    if (currentMemory.usedJSHeapSize > this.MEMORY_THRESHOLD) {
      this.logMemoryInfo(currentMemory);
    }

    // 检查内存增长趋势
    if (this.memoryHistory.length >= 10) {
      const recentHistory = this.memoryHistory.slice(-10);
      const startMemory = recentHistory[0].usedJSHeapSize;
      const endMemory = recentHistory[recentHistory.length - 1].usedJSHeapSize;
      const growthRate = (endMemory - startMemory) / startMemory;

      // 如果内存增长超过50%，发出警告
      if (growthRate > 0.5) {
      }
    }
  }

  /**
   * 获取当前内存使用情况
   */
  getCurrentMemoryUsage(): MemoryInfo | null {
    if (!this.isMemoryAPIAvailable()) return null;

    const memory = (performance as any).memory;
    return {
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit,
      timestamp: Date.now(),
      route: window.location.hash
    };
  }

  /**
   * 获取内存历史记录
   */
  getMemoryHistory(): MemoryInfo[] {
    return [...this.memoryHistory];
  }

  /**
   * 清理内存历史记录
   */
  clearHistory() {
    this.memoryHistory = [];
  }

  /**
   * 格式化字节数
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * 记录内存信息
   */
  private logMemoryInfo(memoryInfo: MemoryInfo) {
  }

  /**
   * 强制垃圾回收（仅在开发环境下可用）
   */
  forceGarbageCollection() {
    if ((window as any).gc) {
      (window as any).gc();
    } else {
    }
  }

  /**
   * 生成内存报告
   */
  generateMemoryReport(): string {
    const current = this.getCurrentMemoryUsage();
    if (!current) return '内存API不可用';

    const report = [
      '📊 内存使用报告',
      '================',
      `当前使用: ${this.formatBytes(current.usedJSHeapSize)}`,
      `总分配: ${this.formatBytes(current.totalJSHeapSize)}`,
      `内存限制: ${this.formatBytes(current.jsHeapSizeLimit)}`,
      `使用率: ${((current.usedJSHeapSize / current.jsHeapSizeLimit) * 100).toFixed(2)}%`,
      `当前路由: ${current.route}`,
      '',
      '历史记录:',
      ...this.memoryHistory.slice(-5).map((info, index) => 
        `${index + 1}. ${this.formatBytes(info.usedJSHeapSize)} @ ${new Date(info.timestamp).toLocaleTimeString()}`
      )
    ];

    return report.join('\n');
  }
}

// 创建全局实例
export const memoryMonitor = new MemoryMonitor();

// 开发环境下自动启动监控（已禁用以避免频繁警告）
// if (process.env.NODE_ENV === 'development') {
//   memoryMonitor.startMonitoring();
// }

// 添加全局方法到window对象（便于在控制台调试）
if (typeof window !== 'undefined') {
  (window as any).memoryMonitor = memoryMonitor;
} 